import Alert from '@pelckmans/elementis/components/alert';
import Button from '@pelckmans/elementis/components/button';
import HighlightedItem from '@pelckmans/elementis/components/highlighted-item';
import SelectField from '@pelckmans/elementis/components/select-field';
import TextArea from '@pelckmans/elementis/components/text-area';
import TextField from '@pelckmans/elementis/components/text-field';
import { createRef, useContext, useEffect, useState, type ReactNode } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { AuthRole } from '..';
import IdentityContext from '../../../contexts/IdentityContext';
import { useRoutes } from '../../../hooks/useRoutes';
import { getBrowserInfo } from '../../../utils/browserInfo';
import { buildGroupProps } from '../../../utils/elementisUtils';
import { validateEmail } from '../../../utils/emailValidator';
import { postUserMessage, type UserMessage } from '../api/support';
import useSubjects, { InfoField, type Subject } from '../hooks/useSubjects';
import RequestCodeForm, { type RequestCodeFormError } from './RequestCodeForm';

const BASE = 'PAGES.HELP.SECTIONS.CONTACT';

type ErrorState = 'invalid' | 'unhandled' | 'recaptcha' | 'maxMembersExceeded';

interface FormValues {
  name: string;
  email: string;
  message: string;
  bookTitle: string;
  subject: Subject | null;
  code: string;
  schoolName: string;
  schoolAddress: string;
  question: string;
}

export default function ContactForm({ authRole }: { authRole: AuthRole }) {
  const { REACT_APP_P_STUDIO_GROUP: studioGroup, REACT_APP_RECAPTCHA_SITE_KEY: recaptchaKey } = process.env;
  const { identity } = useContext(IdentityContext);

  const defaultValues = identity.isAuthenticated
    ? {
        name: identity?.fullName,
        email: identity?.email,
      }
    : {};

  const { buildAbsolutePath } = useRoutes();
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    control,
    trigger,
    reset,
    formState: { errors, isValid, isSubmitted },
  } = useForm<FormValues>({
    mode: 'onBlur',
    defaultValues,
  });

  const { getSubjects } = useSubjects({
    isAnonymous: authRole === 'ANONYMOUS',
    isSecondaryTeacher: authRole === 'TEACHER' && ['SO', 'ES'].includes(studioGroup),
  });

  const subjects = getSubjects();

  const [infoFields, setInfoFields] = useState<InfoField[]>([]);
  const [errorState, setErrorState] = useState<ErrorState>();
  const [subjectErrorText, setSubjectErrorText] = useState<string | ReactNode | undefined>();
  const [hasSend, setHasSend] = useState(false);

  const recaptchaRef = createRef<ReCAPTCHA>();

  const onSubmit = async (formValues: FormValues) => {
    if (subjectErrorText) {
      setErrorState('invalid');
      return;
    }

    if (!identity.isAuthenticated && !recaptchaRef?.current?.getValue()) {
      setErrorState('recaptcha');
      return;
    }

    setErrorState(undefined);

    const userMessage: UserMessage = {
      from: {
        name: formValues.name,
        email: formValues.email,
      },
      subject: formValues.subject!.name,
      message: formValues.question,
      info: {},
      technicalInformation: getBrowserInfo(),
      gRecaptchaResponse: recaptchaRef?.current?.getValue(),
    };

    if (infoFields?.includes('schoolName') || infoFields?.includes('schoolAddress')) {
      userMessage.info.school = {};

      if (infoFields?.includes('schoolName')) userMessage.info.school.name = formValues.schoolName;
      if (infoFields?.includes('schoolAddress')) userMessage.info.school.address = formValues.schoolAddress;
    }

    if (infoFields?.includes('bookTitle') && formValues.bookTitle !== '') {
      userMessage.info.bookTitle = formValues.bookTitle;
    }
    if (infoFields?.includes('code') && formValues.code !== '') {
      userMessage.info.code = formValues.code;
    }

    try {
      await postUserMessage(studioGroup, userMessage);

      setHasSend(true);
    } catch (error) {
      setErrorState('unhandled');
      console.error('Error sending user message:', error);
    }
  };

  const onRestartForm = () => {
    reset();
    setInfoFields([]);
    setErrorState(undefined);
    setHasSend(false);
  };

  const handleRequestCodeFormError = (error: RequestCodeFormError) => {
    switch (error) {
      case 'invalid':
        setErrorState('invalid');
        break;
      case 'unhandled':
        setErrorState('unhandled');
        break;
      case 'noTeacherLicense':
        setSubjectErrorText(t(`${BASE}.SUBJECT.ERRORS.NO_TEACHER_LICENSE`));
        break;
      case 'maxMembersExceeded':
        setErrorState('maxMembersExceeded');
        break;
      default:
        console.error('RequestCodeForm error:', error);
    }
  };

  useEffect(() => {
    // Validate subject when the subject changes
    if (control._formValues.subject) trigger('subject');
  }, [control._formValues.subject, trigger, subjectErrorText]);

  useEffect(() => {
    if (isSubmitted && !isValid) setErrorState('invalid');
  }, [isSubmitted, isValid]);

  useEffect(() => {
    if (infoFields.includes('errorContactTeacher')) {
      setSubjectErrorText(t(`${BASE}.SUBJECT.ERRORS.CONTACT_TEACHER`));
    } else if (infoFields.includes('errorNotLoggedIn')) {
      setSubjectErrorText(
        <span
          dangerouslySetInnerHTML={{
            __html: t(`${BASE}.SUBJECT.ERRORS.NOT_LOGGED_IN`, { url: buildAbsolutePath(['LOGIN']) }),
          }}
        />,
      );
    } else if (!infoFields.includes('requestLicenseCodeForm')) {
      // Add this clause to avoid overwriting the RequestCodeForm error
      setSubjectErrorText(undefined);
    }
  }, [buildAbsolutePath, infoFields, t]);

  if (hasSend) {
    return (
      <HighlightedItem
        variant="feedback"
        message={t(`${BASE}.HAS_SEND.MESSAGE`)}
        buttonGroupProps={buildGroupProps(
          <Button prominence="neutral" onClick={onRestartForm}>
            {t(`${BASE}.HAS_SEND.ACTION`)}
          </Button>,
        )}
      />
    );
  }

  return (
    <div className="contact-form">
      {!!errorState && (
        <div className="contact-form__alerts">
          {errorState === 'invalid' && <Alert prominence="danger">{t('FORM.ERROR.INVALID')}</Alert>}
          {errorState === 'unhandled' && <Alert prominence="danger">{t('FORM.ERROR.UNHANDLED')}</Alert>}
          {errorState === 'recaptcha' && <Alert prominence="danger">{t(`${BASE}.ERRORS.CAPTCHA`)}</Alert>}
          {errorState === 'maxMembersExceeded' && (
            <Alert prominence="danger">{t(`${BASE}.MEMBERS.ERRORS.MAX_MEMBERS_EXCEEDED`)}</Alert>
          )}
        </div>
      )}

      <form id="contact-form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <div className="contact-form__fields">
          <TextField
            id="name"
            key="name"
            labelText={t(`${BASE}.NAME.LABEL`)}
            autoComplete="on"
            required
            disabled={defaultValues.name !== undefined}
            error={!!errors.name}
            errorText={errors.name?.message}
            {...register('name', { required: t('FORM.VALIDATION.REQUIRED') })}
          />
          <TextField
            id="email"
            key="email"
            labelText={t(`${BASE}.EMAIL.LABEL`)}
            autoComplete="on"
            required
            disabled={defaultValues.email !== undefined}
            error={!!errors.email}
            errorText={errors.email?.message}
            {...register('email', {
              required: t('FORM.VALIDATION.REQUIRED'),
              validate: value => validateEmail(value) || t(`${BASE}.EMAIL.INVALID`),
            })}
          />
          <Controller
            name="subject"
            control={control}
            rules={{ required: t('FORM.VALIDATION.REQUIRED') }}
            render={({ field, fieldState }) => (
              <SelectField
                labelText={t(`${BASE}.SUBJECT.LABEL`)}
                required
                placeholder={t(`${BASE}.SUBJECT.PLACEHOLDER`)}
                renderValueContent={subject => (subject ? t(subject.translation) : '')}
                dropdownMenuProps={{
                  ariaLabel: t(`${BASE}.SUBJECT.A11Y_LABEL`),
                }}
                error={!!subjectErrorText || !!fieldState.error}
                errorText={subjectErrorText || fieldState.error?.message}
                value={field.value}
                onChange={(subject: Subject) => {
                  field.onChange(subject);
                  setInfoFields(subject.fields[authRole]);
                }}
              >
                {subjects.map(subject => (
                  <SelectField.Item key={subject.name} value={subject} text={t(subject.translation)} />
                ))}
              </SelectField>
            )}
          />
          {infoFields.includes('bookTitle') && (
            <TextField
              id="bookTitle"
              key="bookTitle"
              labelText={t(`${BASE}.BOOK_TITLE.LABEL`)}
              required
              error={!!errors.bookTitle}
              errorText={errors.bookTitle?.message}
              {...register('bookTitle', { required: t('FORM.VALIDATION.REQUIRED') })}
            />
          )}
          {infoFields.includes('code') && (
            <TextField
              id="code"
              key="code"
              labelText={t(`${BASE}.CODE.LABEL`)}
              required
              error={!!errors.code}
              errorText={errors.code?.message}
              {...register('code', { required: t('FORM.VALIDATION.REQUIRED') })}
            />
          )}
          {infoFields.includes('schoolName') && (
            <TextField
              id="schoolName"
              key="schoolName"
              labelText={t(`${BASE}.SCHOOL_NAME.LABEL`)}
              required
              error={!!errors.schoolName}
              errorText={errors.schoolName?.message}
              {...register('schoolName', { required: t('FORM.VALIDATION.REQUIRED') })}
            />
          )}
          {infoFields.includes('schoolAddress') && (
            <TextField
              id="schoolAddress"
              key="schoolAddress"
              labelText={t(`${BASE}.SCHOOL_ADDRESS.LABEL`)}
              required
              error={!!errors.schoolAddress}
              errorText={errors.schoolAddress?.message}
              {...register('schoolAddress', { required: t('FORM.VALIDATION.REQUIRED') })}
            />
          )}
          {infoFields.includes('question') && (
            <TextArea
              id="question"
              key="question"
              labelText={t(`${BASE}.QUESTION.LABEL`)}
              required
              error={!!errors.question}
              errorText={errors.question?.message}
              {...register('question', { required: t('FORM.VALIDATION.REQUIRED') })}
            />
          )}
          {!identity.isAuthenticated && <ReCAPTCHA ref={recaptchaRef} sitekey={recaptchaKey || 'NO_KEY_FOUND'} />}
        </div>

        {!infoFields.includes('requestLicenseCodeForm') && (
          <div className="contact-form__submit">
            <Button prominence="success" type="submit">
              {t(`${BASE}.ACTIONS.SUBMIT`)}
            </Button>
          </div>
        )}
      </form>

      {infoFields.includes('requestLicenseCodeForm') && (
        <RequestCodeForm
          onError={handleRequestCodeFormError}
          onSuccess={() => setHasSend(true)}
          studioGroup={studioGroup}
        />
      )}
    </div>
  );
}
